/**
 * Display formatting for verification-result values whose stored form is not what a person
 * should read.
 *
 * Kept beside `sessionDateTime.ts` and for the same reason: these rules were being made
 * independently inside each step's Detail component, which is how the same field ended up
 * rendering differently depending on which card you were looking at.
 */

/**
 * Gender codes → the word.
 *
 * Aadhaar (and the ID/MSME providers) return a single letter, so the card read "Gender: M".
 * That is a database value on a page a human reviews.
 *
 * `T` is here deliberately and must stay: Transgender is a real, selectable Aadhaar gender,
 * so dropping it or folding it into "Other" would misgender a real applicant on their own
 * KYC record. `O` is mapped separately because some providers send it in place of `T`.
 */
const GENDER_WORDS: Record<string, string> = {
  m: 'Male',
  male: 'Male',
  f: 'Female',
  female: 'Female',
  t: 'Transgender',
  transgender: 'Transgender',
  o: 'Other',
  other: 'Other',
};

/**
 * The readable gender for a stored value.
 *
 * An unrecognised value is returned UNCHANGED rather than mapped to "Other" or blanked. If a
 * provider sends something this table has not seen, showing it verbatim lets a reviewer see
 * what actually arrived; guessing would put a gender on the record that nobody reported.
 */
export function formatGender(value: unknown): string {
  const raw = String(value ?? '').trim();
  return GENDER_WORDS[raw.toLowerCase()] ?? raw;
}

/**
 * An Indian mobile number with its country code — `8320402039` → `+91 8320402039`.
 *
 * We store the bare ten digits the applicant typed, because the OTP gate asks for exactly
 * that. On screen it read as a plain number with no indication of which country it belongs
 * to, which matters the moment anyone dials it or pastes it into a support tool.
 *
 * DELIBERATELY CONSERVATIVE. `+91` is added only when the digits are unambiguously an Indian
 * mobile — ten digits starting 6, 7, 8 or 9, which is the range TRAI allocates to mobiles.
 * Anything else is returned untouched, because stamping `+91` onto a number that is not
 * Indian would be writing a wrong country code onto a KYC record, and a number nobody can
 * ring is better than one that rings the wrong person.
 *
 * Numbers that ALREADY carry the code (`+918320402039`, `918320402039`, `08320402039`) are
 * normalised to the same shape rather than being left in a third format or double-prefixed.
 */
export function formatMobile(value: string | null | undefined): string | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;

  // Strip only formatting characters. A letter or any other stray character means this is
  // not a phone number we understand, and the guard below returns it as-is.
  const digits = raw.replace(/[\s\-()]/g, '');

  // Already prefixed, in any of the three ways it can reach us.
  const bare = digits.startsWith('+91')
    ? digits.slice(3)
    : digits.length === 12 && digits.startsWith('91')
      ? digits.slice(2)
      : digits.length === 11 && digits.startsWith('0')
        ? digits.slice(1) // the trunk prefix used when dialling domestically
        : digits;

  return INDIAN_MOBILE.test(bare) ? `+91 ${bare}` : raw;
}

/** Ten digits opening 6-9 — TRAI's mobile range. Landlines and short codes do not match. */
const INDIAN_MOBILE = /^[6-9]\d{9}$/;
